import { useState } from 'react';

import { t } from '@web/i18n';
import { AppIcons, Card, IconButton } from '@web/ui/core/atoms';
import { initials } from '@web/ui/core/utils';

/**
 * UI-facing org entry — the trio of fields OrgSwitcher needs to
 * render an org row. Defined locally so the organism does not pull
 * the repository-layer `OrgEntry` shape across layer boundaries.
 */
type OrgSwitcherEntry = {
  id: string;
  slug: string;
  name: string;
  role: 'owner' | 'admin' | 'member' | 'guest';
};

function OrgGlyph({ org }: Readonly<{ org: OrgSwitcherEntry }>): React.JSX.Element {
  return (
    <span
      aria-hidden
      className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-border-soft bg-card font-mono text-2xs font-medium text-foreground"
    >
      {initials(org.name).slice(0, 2)}
    </span>
  );
}

function OrgRoleLabel({ role }: Readonly<{ role: OrgSwitcherEntry['role'] }>): React.JSX.Element {
  return (
    <span className="block truncate text-card-id text-muted-foreground">
      {t(`profileMenu.role.${role}`)}
    </span>
  );
}

export interface OrgSwitcherProps {
  active: OrgSwitcherEntry;
  others: OrgSwitcherEntry[];
  onSwitch: (slug: string) => void;
}

/**
 * OrgSwitcher — the "current org / switch to another" widget that
 * lives inside the profile menu. Renders the active org in a Card
 * with a trailing toggle that expands a small list of other orgs.
 * The list collapses again after the caller commits a switch.
 */
export function OrgSwitcher({
  active,
  others,
  onSwitch,
}: Readonly<OrgSwitcherProps>): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const hasOthers = others.length > 0;

  return (
    <div>
      <Card>
        <div className="flex items-center gap-2 py-1 pl-1.5 pr-1">
          <OrgGlyph org={active} />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-meta font-medium text-foreground">{active.name}</span>
            <OrgRoleLabel role={active.role} />
          </span>
          {hasOthers ? (
            <IconButton
              icon="arrows-left-right"
              variant={open ? 'subtle' : 'ghost'}
              size="xs"
              aria-label={t('profileMenu.org.switchAria')}
              title={t('profileMenu.org.switchAria')}
              onClick={() => {
                setOpen((o) => !o);
              }}
            />
          ) : null}
        </div>
      </Card>

      {open && hasOthers ? (
        <div
          role="menu"
          aria-label={t('profileMenu.org.switchAria')}
          className="mt-2 flex flex-col gap-1"
        >
          {others.map((o) => (
            <Card
              key={o.id}
              onTap={() => {
                setOpen(false);
                onSwitch(o.slug);
              }}
              aria-label={o.name}
            >
              <div className="flex items-center gap-2 px-2 py-1.5">
                <OrgGlyph org={o} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-meta font-medium text-foreground">{o.name}</span>
                  <OrgRoleLabel role={o.role} />
                </span>
                <AppIcons name="arrow-right" size={11} className="text-faint-foreground" />
              </div>
            </Card>
          ))}
        </div>
      ) : null}
    </div>
  );
}
